import React, { useState, useMemo } from 'react'
import { EditBoard } from 'Assets/icons/EditBoard'
import { FlexRow } from 'Providers/app.styled'
import { ActionText, ActionDescription } from '../PlanDetails/PlanDetails.styled'
import { useTranslation } from 'react-i18next'
import { CancelPlanModal } from '../Modals/CancelModal/CancelModal'
import { CancelProductProps, useCancelProduct } from 'Features/accountSettings/api/cancelProduct'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { ProductTypesWithoutBundle } from 'Types/ProductTypes'
import { CancelPlanRequestProps } from 'Features/accountSettings/api/cancelPlan'
import { getProductName } from 'Utils/productName'
import { useIsCancelProductFlow } from 'Features/accountSettings/hooks/useIsCancelProductFlow'
import { useRevokeProductCancelation } from 'Features/accountSettings/api/revokeProductCancelation'
import { RevokeProductCancelationConfirmation } from '../Modals/RevokeProductCancelationConfirmation'
import { VerifyProductCancellationModal } from '../Modals/VerifyProductCancellationModal'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import { ExtendTrialModal } from '../Modals/ExtendTrialModal'
import { useExtendTrial } from 'Features/accountSettings/api/extendTrial'
import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'

/**
 * CANCEL PRODUCT FLOW
 *
 * hide cancel product if:
 * - user has only one product
 *
 * Show EXTEND_TRIAL first then CANCEL_PRODUCT:
 * - if user account is still in trial and has no extend account request
 * - if account is active and the product is in trial allowed trial exaction
 *
 * Show CANCEL_PRODUCT first then VERIFY_CANCEL_PRODUCT:
 * - if user account is active
 *
 * Show REVOKE_CANCEL_PRODUCT:
 * - if user has productCancelled in plan details data
 */

const MODAL_OPTIONS = {
  EXTEND_TRIAL: 'EXTEND_TRIAL',
  CANCEL_PRODUCT: 'CANCEL_PRODUCT',
  VERIFY_CANCEL_PRODUCT: 'VERIFY_CANCEL_PRODUCT',
  REVOKE_CANCEL_PRODUCT: 'REVOKE_CANCEL_PRODUCT',
} as const

type ModalOption = (typeof MODAL_OPTIONS)[keyof typeof MODAL_OPTIONS] | null

export const CancelProduct = () => {
  const { t } = useTranslation('accountSettings')
  const { getProductId } = useIsCancelProductFlow()
  const cancelProduct = useCancelProduct()
  const isTrialSubscription = useIsTrialSubscription()
  const { productsInTrial, isExtendTrialAllowed } = useIsProductTrial()
  const revokeCancelation = useRevokeProductCancelation()
  const extendTrail = useExtendTrial()
  const { data: plansDetailsData } = useGetPlansDetails()
  const [modalToShow, setModalToShow] = useState<ModalOption>(null)
  const [product, setProduct] = useState<ProductTypesWithoutBundle | undefined>(undefined)
  const [form, setForm] = useState<CancelProductProps | undefined>(undefined)

  const revokeCancelationProductName: ProductTypesWithoutBundle | undefined = useMemo(() => {
    for (const key in plansDetailsData?.productCancelled || {})
      if (plansDetailsData?.productCancelled[key]) return key as ProductTypesWithoutBundle
    return undefined
  }, [plansDetailsData])

  const translate = (key: string) =>
    t(`plan_details.${key}`, { product: getProductName(revokeCancelationProductName) })

  const closeModal = () => setModalToShow(null)

  const ctaText = translate(
    revokeCancelationProductName
      ? 'revoke_product_cancellation'
      : isTrialSubscription || productsInTrial
      ? 'cancel_product'
      : 'cancel_product_renewal'
  )

  const ctaDescriptionText = translate(
    revokeCancelationProductName
      ? 'revoke_product_cancellation_description'
      : 'cancel_product_description'
  )

  const onActionClick = () => {
    const showExtendTrialModal =
      (isTrialSubscription && !plansDetailsData?.trial?.extendRequest) ||
      isExtendTrialAllowed(productsInTrial || undefined)

    if (showExtendTrialModal) setModalToShow(MODAL_OPTIONS.EXTEND_TRIAL)
    else
      revokeCancelationProductName
        ? setModalToShow(MODAL_OPTIONS.REVOKE_CANCEL_PRODUCT)
        : setModalToShow(MODAL_OPTIONS.CANCEL_PRODUCT)
  }

  const onExtendTrial = () => {
    extendTrail.mutate(undefined, {
      onSuccess: () => {
        setTimeout(() => closeModal(), 1000)
      },
    })
  }

  const onConfirmCancelModal = ({ reason, description }: CancelPlanRequestProps): void => {
    const requestForm = { reason, description, productId: getProductId(product) }
    const shouldShowVerifyModal = true

    if (shouldShowVerifyModal) {
      setForm(requestForm)
      setModalToShow(MODAL_OPTIONS.VERIFY_CANCEL_PRODUCT)
    } else cancelProduct.mutate(requestForm, { onSuccess: () => setModalToShow(null) })
  }

  return (
    <>
      <FlexRow style={{ marginTop: '28px' }}>
        <EditBoard />
        <ActionText onClick={onActionClick}>{ctaText}</ActionText>
      </FlexRow>
      <ActionDescription>{ctaDescriptionText}</ActionDescription>

      {modalToShow === MODAL_OPTIONS.CANCEL_PRODUCT && (
        <CancelPlanModal
          isOpen
          cancelProduct
          loading={cancelProduct.isLoading}
          setIsOpen={closeModal}
          onConfirm={onConfirmCancelModal}
          product={product}
          setProduct={setProduct}
        />
      )}
      {modalToShow === MODAL_OPTIONS.REVOKE_CANCEL_PRODUCT && (
        <RevokeProductCancelationConfirmation
          isOpen
          loading={revokeCancelation.isLoading}
          setIsOpen={closeModal}
          onConfirm={() =>
            revokeCancelation.mutate(getProductId(revokeCancelationProductName), {
              onSuccess: () => setModalToShow(null),
            })
          }
        />
      )}
      {modalToShow === MODAL_OPTIONS.VERIFY_CANCEL_PRODUCT && (
        <VerifyProductCancellationModal
          isOpen
          productName={getProductName(product)}
          loading={cancelProduct.isLoading}
          setIsOpen={closeModal}
          onCancel={closeModal}
          onConfirm={() => form && cancelProduct.mutate(form, { onSuccess: () => closeModal() })}
        />
      )}
      {modalToShow === MODAL_OPTIONS.EXTEND_TRIAL && (
        <ExtendTrialModal
          isOpen
          setIsOpen={closeModal}
          onConfirm={onExtendTrial}
          onCancelAccount={() => setModalToShow(MODAL_OPTIONS.CANCEL_PRODUCT)}
          loading={extendTrail.isLoading || cancelProduct.isLoading}
        />
      )}
    </>
  )
}
